/* eslint-disable no-restricted-imports */
import React, {createContext, useContext, ReactNode, useState} from 'react'
import {TranslationObject} from '@colonist/i18n'
import {CA} from '../../../../../code/client/shared/scripts/analytics/Analytics'
import {useUIGameSocketContext} from '../../contexts/UIGameSocketContext'
import {getHomepageUrl, navigateToPage} from '../../../../../code/client/shared/scripts/ui_utils/ClientUtils'
import {UIPopupController} from '../../../../../code/client/shared/scripts/ui_popup/UIPopupController'
import {ViewPopupOptions} from '../../../../../code/client/shared/scripts/ui_popup/ViewPopupOptions'
import {FeedbackFormUtils} from '../../../../../code/client/shared/scripts/ui_popup/ui_popup_feedback_form/FeedbackFormUtils'
import {UIFeedbackFormPopupController} from '../../../../../code/client/shared/scripts/ui_popup/ui_popup_feedback_form/UIFeedbackFormPopupController'
import {FeedbackFormLocationEnum} from '../../../../../code/shared/entity_datas/EntityDatas'
import {socket, uiManagerEvents} from '../../../../../code/client/shared/scripts/EntryShared'
import {UIUrlParameterNames} from '../../../../../code/client/shared/scripts/ui_html/UIUrlController'
import {UIEntryUrlParameterController} from '../../../../../code/client/shared/scripts/UIEntryUrlParameterController'
import {useGameStore} from '../../store/GameStoreContext'

export const UIGameHelpLeaveGameContext = createContext<{
    canResign: boolean
    leaveGame: () => void
} | undefined>(undefined)

export const useUIGameHelpLeaveGameContext = () => {
    const context = useContext(UIGameHelpLeaveGameContext)
    if(!context) throw new Error('useUIGameHelpLeaveGameContext must be used within a UIGameHelpLeaveGameProvider')
    return context
}

interface UIGameHelpLeaveGameProviderProps {
    children: ReactNode
    initialCanResign: boolean
}

let globalSetCanResign: ((canResign: boolean) => void) | undefined
export const setGlobalCanResign = (canResign: boolean) => {
    if(globalSetCanResign) globalSetCanResign(canResign)
}

export const UIGameHelpLeaveGameProvider = ({children, initialCanResign}: UIGameHelpLeaveGameProviderProps) => {
    const [canResign, setCanResign] = useState(initialCanResign)
    const {sendLeaveGame, sendResignGame} = useUIGameSocketContext()
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)

    globalSetCanResign = setCanResign

    const leaveGame = () => {
        const isViewingReplayOnProfilePage = window.location.href.includes('/profile')
        if(isViewingReplayOnProfilePage) {
            location.reload()
            return
        }

        if(canResign) {
            createResignGamePopup()
            return
        }

        createStandardLeaveGamePopup()
    }

    const createStandardLeaveGamePopup = () => {
        const title: TranslationObject = {key: 'strings:game.leaveGame.title'}
        const body: TranslationObject = {key: 'strings:game.leaveGame.body'}

        CA.sendGoogleAnalyticsEvent(CA.Category.Game, CA.Label.ClickedGameLobbyButton)

        const leaveGameAction = () => showPopupsBeforeExitingGame(() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.Game, CA.Label.ClickedGameLobbyButtonAccept)
            sendLeaveGame()
            if(isMyColorSpectator) {
                uiManagerEvents.handleUrlRedirectViaParameters(UIUrlParameterNames.Spectate, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE)
                return
            }
            navigateToPage(getHomepageUrl())
        })

        UIPopupController.create(title, body, leaveGameAction, () => undefined)
    }

    const createResignGamePopup = () => {
        const title: TranslationObject = {key: 'strings:game.resign.title'}
        const body: TranslationObject = {key: 'strings:game.resign.body'}

        const popupOptions: ViewPopupOptions = {
            showXButton: true,
            showCheckButton: true,
            xButtonCustomContent: {key: 'strings:colonistCommon.buttons.cancel'},
            checkButtonCustomContent: {key: 'strings:game.resign.title'},
        }

        const cancelAction = () => undefined
        const resignAction = () => showPopupsBeforeExitingGame(() => sendResignGame())

        UIPopupController.create(title, body, resignAction, cancelAction, true, popupOptions)
    }

    const showPopupsBeforeExitingGame = (continuationAction: () => void) => {
        const shouldShowFeedbackForm = FeedbackFormUtils.shouldShowFeedbackForm()
        if(shouldShowFeedbackForm == true) {
            new UIFeedbackFormPopupController(() => continuationAction(), socket.gameListener.databaseGameId, FeedbackFormLocationEnum.QuitGame)
            return
        }
        continuationAction()
    }

    return (
        <UIGameHelpLeaveGameContext.Provider value={{
            canResign: canResign,
            leaveGame: leaveGame,
        }}>
            {children}
        </UIGameHelpLeaveGameContext.Provider>
    )
}
